<script>
    import {onMount} from 'svelte';
    import BattlePassMilestone from './BattlePassMilestone.svelte';
    import {milestonesFromEvent} from './utils.js';

    let { event, onclaimed, onunlockpremium } = $props();

    let trackEl = $state(null);

    let milestones = $derived(milestonesFromEvent(event));

    let rail = $state(null);

    // Measure the rail and each medallion's center, so the fill can land
    // exactly on a node (positions are static — only re-measured on resize).
    function positionRail() {
        if (!trackEl) return;
        const nodes = trackEl.querySelectorAll('.milestone');
        if (!nodes.length) return;
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        const node = first.querySelector('.node-wrapper');
        const firstCard = first.querySelector('.reward-card');
        const lastCard = last.querySelector('.reward-card');
        if (!node || !firstCard || !lastCard) return;
        const left = first.offsetLeft + firstCard.offsetLeft;
        const right = last.offsetLeft + lastCard.offsetLeft + lastCard.offsetWidth;
        // center x of each medallion, relative to the rail's left edge
        const nodeCenters = [...nodes].map((m) => {
            const nw = m.querySelector('.node-wrapper');
            return m.offsetLeft + nw.offsetLeft + nw.offsetWidth / 2 - left;
        });
        rail = {
            top: first.offsetTop + node.offsetTop + node.offsetHeight / 2 - 5,
            left,
            width: right - left,
            nodeCenters,
        };
    }

    // Fill width in px: interpolate between adjacent medallion centers using
    // the real point thresholds, so the fill edge sits precisely where the
    // player's points fall on the track (not on equal-width segments).
    let fillStyle = $derived.by(() => {
        if (!rail) {
            // pre-measurement fallback: equal-segment percentage
            const n = milestones.length;
            if (n === 0) return 'width: 0';
            const reached = milestones.filter((m) => m.reached).length;
            if (reached >= n) return 'width: 100%';
            const prev = reached === 0 ? 0 : milestones[reached - 1].pointsNeeded;
            const next = milestones[reached].pointsNeeded;
            const frac = Math.max(0, (event.points - prev) / (next - prev));
            return `width: ${((reached + frac) / n) * 100}%`;
        }
        const centers = rail.nodeCenters;
        const thresholds = milestones.map((m) => m.pointsNeeded);
        const pts = event.points;
        const lastIdx = thresholds.length - 1;
        let px;
        if (pts <= 0) {
            px = 0;
        } else if (pts >= thresholds[lastIdx]) {
            px = centers[lastIdx];
        } else {
            // first threshold strictly greater than current points
            const k = thresholds.findIndex((t) => pts < t);
            const prevThr = k === 0 ? 0 : thresholds[k - 1];
            const prevPos = k === 0 ? 0 : centers[k - 1];
            const frac = (pts - prevThr) / (thresholds[k] - prevThr);
            px = prevPos + frac * (centers[k] - prevPos);
        }
        return `width: ${px}px`;
    });

    onMount(() => {
        if (!trackEl) return;
        positionRail();
        const currentEl = trackEl.querySelector('.milestone.current');
        if (currentEl) {
            trackEl.scrollLeft = currentEl.offsetLeft - trackEl.clientWidth / 2 + currentEl.clientWidth / 2;
        }
        window.addEventListener('resize', positionRail);
        return () => window.removeEventListener('resize', positionRail);
    });
</script>

<div class="track-wrapper">
    <div class="track" bind:this={trackEl}>
        <div class="progress-bar"
             style={rail ? `top: ${rail.top}px; left: ${rail.left}px; width: ${rail.width}px` : ''}>
            <div class="fill" style={fillStyle}></div>
        </div>
        {#each milestones as milestone (milestone.key)}
            <BattlePassMilestone {milestone} {event} {onclaimed} {onunlockpremium}/>
        {/each}
    </div>
</div>

<style lang="scss">
  .track-wrapper {
    width: 100%;
    margin: 0 auto;
    border-radius: 12px;
    background: linear-gradient(180deg, rgb(255, 231, 140) 0%, rgb(244, 208, 116) 100%);
    border: 1px solid #8a6228;
    box-shadow: inset 0 0 40px rgba(120, 70, 20, 0.3);
  }

  .track {
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: stretch;
    overflow-x: auto;
    padding: 12px 16px 16px;
    scroll-behavior: smooth;
    scrollbar-width: thin;
    scrollbar-color: #8a6228 rgba(120, 70, 20, 0.25);

    &::-webkit-scrollbar {
      height: 8px;
    }

    &::-webkit-scrollbar-track {
      background: rgba(120, 70, 20, 0.25);
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: #8a6228;
      border-radius: 4px;
    }

    .progress-bar {
      position: absolute;
      // position is measured at runtime (see positionRail); these are only
      // pre-measurement fallbacks: top = track pad 12 + milestone pad 14
      // + free card 88 + gap 10 + node half 28 - rail half 5; left/width
      // flush with the reward cards (track pad 16 + milestone pad 8)
      top: 147px;
      left: 24px;
      width: calc(230px * 10 - 16px);
      height: 10px;
      background: linear-gradient(180deg, #3b1305 0%, #5a2c0e 100%);
      border-radius: 5px;
      border: 1px solid rgba(138, 98, 40, 0.7);
      box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.55);

      .fill {
        height: 100%;
        background: linear-gradient(90deg, #ffcc00 0%, #ff7700 100%);
        border-radius: 5px;
        box-shadow: 0 0 10px rgba(255, 150, 0, 0.7);
        transition: width 0.4s ease;
      }
    }
  }

  @media (max-width: 767px) {
    .track {
      flex-direction: column;
      overflow-x: visible;
      padding: 6px 8px 10px;

      .progress-bar {
        display: none;
      }
    }
  }
</style>
